import { BaseEditDialog } from '@features/user-account/components/BaseEditDialog';
import { StyledIconButton } from '@features/user-account/components/StyledIconButton';
import { useAdminApi } from '@hooks/useAdminApi';
import { Box, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { LinkSide, Organization, OrganizationLink } from '@protos/organization';
import { PRODUCT_GROUP_OPTIONS, ProductGroupEnum } from '@protos/user';
import { TextField } from '@shared/components/TextField';
import { TextInputSelectBox } from '@shared/components/TextFieldSelectBox';
import { TextInputReadOnlyBox } from '@shared/components/TextInputReadOnlyBox';
import { useMemo, useState } from 'react';
import { useOrganizationOptions } from '@hooks/useOrganizationOptions';

interface EditOrganizationLinkDialogProps {
  organization: Organization;
  side: LinkSide;
  link?: OrganizationLink;
  // The links already on this side. The PUT upserts on (agent, client, group), so
  // without these the Add flow would overwrite an existing grant's scope and enabled flag
  links: OrganizationLink[];
  isOpen: boolean;
  onClose: () => void;
  onLinkSaved: () => void;
}

const optionLabel = (organization: Organization) => `${organization.name} (#${organization.id})`;

export const EditOrganizationLinkDialog: React.FC<EditOrganizationLinkDialogProps> = ({
  organization,
  side,
  link,
  links,
  isOpen,
  onClose,
  onLinkSaved,
}) => {
  const { apiClient } = useAdminApi();
  const { organizations } = useOrganizationOptions();

  const [counterpartyId, setCounterpartyId] = useState<number | undefined>(
    link ? (side === 'agent' ? link.client_organization_id : link.agent_organization_id) : undefined
  );
  const [scopeProductGroup, setScopeProductGroup] = useState<ProductGroupEnum>(link?.scope_product_group ?? PRODUCT_GROUP_OPTIONS[0]);
  const [scopeProducts, setScopeProducts] = useState<string[]>(link?.scope_products ?? []);
  const [enabled, setEnabled] = useState(link?.enabled ?? true);

  // Counterparties already linked on the selected group. The key is (agent, client,
  // group), so re-adding one is an upsert that silently rewrites its scope products and
  // enabled flag. Group is part of the key, so this is recomputed when the group changes.
  const alreadyLinked = useMemo(
    () =>
      new Set(
        links
          .filter(existing => existing.scope_product_group === scopeProductGroup)
          .map(existing => (side === 'agent' ? existing.client_organization_id : existing.agent_organization_id))
      ),
    [links, scopeProductGroup, side]
  );

  // An organization cannot be its own client, so it is never its own counterparty
  const counterpartyOptions = useMemo(
    () => organizations.filter(o => o.id !== organization.id && !alreadyLinked.has(o.id)).map(optionLabel),
    [organizations, organization.id, alreadyLinked]
  );

  const counterpartyValue = useMemo(() => {
    const found = organizations.find(o => o.id === counterpartyId);
    return found ? optionLabel(found) : '';
  }, [organizations, counterpartyId]);

  const onCounterpartyChange = (label: string) => {
    const found = organizations.find(o => optionLabel(o) === label);
    setCounterpartyId(found?.id);
  };

  const onSave = async () => {
    if (!apiClient) return;
    if (!counterpartyId) throw new Error('Pick an organization for the other side of the link');
    // Reachable without touching the picker: choosing a counterparty and then changing
    // the group can land on a pair that is already linked
    if (!link && alreadyLinked.has(counterpartyId))
      throw new Error(`${counterpartyValue} is already linked on ${scopeProductGroup}. Edit that link instead, or delete it first`);
    await apiClient.upsertOrganizationLink({
      agent_organization_id: side === 'agent' ? organization.id : counterpartyId,
      client_organization_id: side === 'agent' ? counterpartyId : organization.id,
      scope_product_group: scopeProductGroup,
      scope_products: scopeProducts.filter(symbol => !!symbol.trim()),
      enabled,
    });
    onLinkSaved();
  };

  const onScopeProductAdd = () => setScopeProducts([...scopeProducts, '']);

  const onScopeProductRemove = (id: number) => {
    const updated = [...scopeProducts];
    updated.splice(id, 1);
    setScopeProducts(updated);
  };

  const onScopeProductUpdate = (id: number, value: string) => {
    const updated = [...scopeProducts];
    updated[id] = value;
    setScopeProducts(updated);
  };

  const counterpartyLabel = side === 'agent' ? 'Client Organization' : 'Agent Organization';

  return (
    <BaseEditDialog
      title={link ? 'Edit Organization Link' : 'Add Organization Link'}
      isOpen={isOpen}
      onClose={onClose}
      clearErrorOn={[counterpartyId, scopeProductGroup, scopeProducts, enabled]}
      defaultErrorMessage="There was an error saving the organization link"
      onSave={onSave}
    >
      <Box display="flex" flexDirection="column" marginRight={2} gap={1} minWidth="420px">
        <Typography fontSize={11}>
          The agent side must already carry the AGENT type. A named product must exist and sit in the link&apos;s group: the group alone already
          covers every symbol in it.
        </Typography>
        {/* The two organizations and the group key the link, so on an edit they are shown,
            not changed: a PUT with a different key would add a row and leave this one behind.
            Delete and re-add to move a link to another counterparty or group. */}
        {link ? (
          <>
            <TextInputReadOnlyBox label={counterpartyLabel} value={counterpartyValue} sx={{ width: '360px' }} />
            <TextInputReadOnlyBox label="Product Group" value={scopeProductGroup} sx={{ width: '360px' }} />
          </>
        ) : (
          <>
            <TextInputSelectBox
              label={counterpartyLabel}
              value={counterpartyValue}
              sx={{ width: '360px' }}
              onChange={onCounterpartyChange}
              options={counterpartyOptions}
            />
            <TextInputSelectBox
              label="Product Group"
              value={scopeProductGroup}
              sx={{ width: '360px' }}
              onChange={(value: ProductGroupEnum) => {
                setScopeProductGroup(value);
                // The picker is scoped to the group, so the current pick can disappear
                // from its own options. Clear it rather than leave a value with no option.
                const counterpartyOf = (existing: OrganizationLink) =>
                  side === 'agent' ? existing.client_organization_id : existing.agent_organization_id;
                if (links.some(existing => existing.scope_product_group === value && counterpartyOf(existing) === counterpartyId)) {
                  setCounterpartyId(undefined);
                }
              }}
              options={PRODUCT_GROUP_OPTIONS}
            />
          </>
        )}
        <FormControlLabel
          control={<Switch checked={enabled} onChange={event => setEnabled(event.target.checked)} size="small" />}
          label={<Typography fontSize={11}>Enabled{enabled ? '' : ' (the agent has no authority while this is off)'}</Typography>}
        />
        <Stack direction="column" alignItems="flex-start">
          <Typography fontSize={11}>Scope products (optional, in addition to the group)</Typography>
          {scopeProducts.map((symbol, id) => (
            <Stack key={`scope_product_${id}`} direction="row" alignItems="center">
              <TextField
                label="Symbol"
                value={symbol}
                sx={{ width: '200px' }}
                onChange={(value: string) => onScopeProductUpdate(id, value.toLocaleLowerCase())}
              />
              <StyledIconButton onClick={() => onScopeProductRemove(id)} size={'small'} style={{ marginLeft: '5px' }}>
                <i className={'ri-delete-bin-line'} />
              </StyledIconButton>
            </Stack>
          ))}
          <StyledIconButton onClick={onScopeProductAdd}>
            <i className={'ri-add-line'} />
          </StyledIconButton>
        </Stack>
      </Box>
    </BaseEditDialog>
  );
};
